/**
 * Standard ecommerce template config.
 *
 * Drives every customer-facing string, image, category, and price on the
 * `template-ecommerce/*` page set. Nothing in those page files is meant to be
 * clothing-specific — when you swap the config (e.g. via `getTemplateConfig`),
 * the same template re-skins into a coffee / candles / skincare / etc. store.
 *
 * Shape (top-level keys):
 *   business         basic identity (name, tagline, supportEmail, location)
 *   brand            colours + fonts (Montserrat is the global default)
 *   hero             top of Home (overline, heading, subheading, CTA, image)
 *   copy             every block of UI copy — home/shop/productDetail/cart/checkout/orderSuccess/cart
 *   categories       [{ key, label, image, image_prompt }]
 *   reviews          [{ name, text, rating }] — Home page testimonials
 *   trustBadges      [{ icon, title, body }] — ProductDetail trust row + checkout
 *   newsletter       Home newsletter strip
 *   pricing          currency, free-shipping threshold, flat shipping
 *   variantAxesDefaults  [{ key, label, values }] - fallback variant axes when a product
 *                        doesn't declare its own (e.g. "Size", "Colour", "Roast", ...)
 *
 * Image fields can carry either a real URL (when the customer uploads one) or
 * an empty string + `*_image_prompt` so the existing AI imager fills them in.
 */

// ────────────────────────────────────────────────────────────────────────────
// DEFAULT CONFIG — generic "My Store" baseline. Every preset inherits this
// and overrides only what's different.
// ────────────────────────────────────────────────────────────────────────────

export const DEFAULT_TEMPLATE_CONFIG = {
  business: {
    name: "My Store",
    tagline: "Quality products, thoughtfully made.",
    supportEmail: "hello@example.com",
    location: "",
  },
  brand: {
    primaryColor: "#000000",
    accentColor: "#E60000",
    bgColor: "#ffffff",
    textColor: "#0F172A",
    mutedTextColor: "#737373",
    softBgColor: "#FAFAFA",
    fontFamily: "'Montserrat', system-ui, sans-serif",
    displayFontFamily: "'Montserrat', system-ui, sans-serif",
  },
  hero: {
    overline: "— New collection",
    heading: "Made for the everyday.",
    subheading: "Crafted with care. Delivered to your door.",
    ctaText: "Shop now",
    ctaLink: "/shop",
    secondaryCtaText: "",
    secondaryCtaLink: "",
    statValue: "",
    statLabel: "",
    image: "",
    image_prompt: "Editorial lifestyle hero image for a modern online store — soft natural daylight, neutral palette, premium retail mood. No text, no logos.",
  },
  copy: {
    home: {
      featuredOverline: "— New arrivals",
      featuredHeading: "Featured products.",
      featuredViewAll: "View all",
      categoryOverline: "— Shop the categories",
      categoryHeading: "Shop by category.",
      editorialOverline: "— Our story",
      editorialHeading: "Quietly built, made to last.",
      editorialBody: "Every product begins with the same question: would we want this in our own home? If the answer is no, we don't make it.",
      editorialCta: "Read more",
      editorialLink: "/about",
      editorialImage: "",
      editorialImagePrompt: "Editorial brand photograph — warm natural light, magazine quality, calm composition. No text, no logos.",
      reviewsOverline: "— Real customers",
      reviewsHeading: "Loved by our community.",
      reviewsAverage: "★ 4.9 / 5 average",
    },
    shop: {
      overline: "— The collection",
      defaultHeading: "Shop All",
      allLabel: "All",
      sortFeatured: "Featured",
      sortPriceAsc: "Price: Low → High",
      sortPriceDesc: "Price: High → Low",
      sortRating: "Top Rated",
      filtersLabel: "Filters",
      priceRangeLabel: "Price range",
      priceMin: 0,
      priceMax: 300,
      priceStep: 10,
      showingLabel: "Showing",
      productCountSuffix: "products",
      emptyTitle: "No products found.",
      emptyBody: "Try a different category or sort option.",
      searchResultsPrefix: "Results for",
      searchPlaceholder: "Search products...",
    },
    productDetail: {
      reviewsSuffix: "reviews",
      saleBadge: "Sale",
      stockUrgencyTemplate: "Only {n} left in stock",
      addToCartText: "Add to cart",
      sizeGuideText: "Variant guide",
      relatedHeading: "You might also like.",
      bullets: [
        "Premium materials",
        "Crafted with care",
        "Backed by our guarantee",
        "Designed to last",
      ],
      taxNote: "Tax included. Free shipping over $100.",
      breadcrumbHome: "Home",
    },
    cart: {
      title: "Your cart",
      emptyTitle: "Your cart is empty.",
      emptyBody: "Add a product before checking out.",
      emptyCta: "Browse products",
      emptyCtaLink: "/shop",
      continueShoppingText: "Continue shopping",
    },
    checkout: {
      backLink: "Continue shopping",
      title: "Secure Checkout",
      subtitle: "Encrypted · Guest checkout enabled",
      contactSectionTitle: "Contact",
      shippingSectionTitle: "Shipping",
      paymentSectionTitle: "Payment",
      paymentDemoNoteTitle: "Demo checkout",
      paymentDemoNote: "Payments are not enabled in this demo. Placing the order will create a mock confirmation only.",
      placeOrderText: "Place order",
      placingText: "Placing order...",
      summaryTitle: "Order Summary",
      subtotalLabel: "Subtotal",
      shippingLabel: "Shipping",
      totalLabel: "Total",
      freeShippingLabel: "FREE",
      countries: ["United States", "Canada", "United Kingdom", "Australia", "Other"],
      errorMessage: "Could not place order. Try again.",
      successToastTitle: "Order placed!",
    },
    orderSuccess: {
      confirmedTag: "Confirmed",
      title: "Thanks for the order.",
      preparingTemplate: "Your order #{n} is being prepared.",
      whatsNextTitle: "What's next",
      emailLine: "You'll receive a confirmation email with your receipt and order details.",
      shippingLine: "Your order ships within 24 hours. Tracking will arrive by email.",
      orderDetailsTitle: "Order Details",
      shippingToLabel: "Shipping to:",
      continueShoppingText: "Continue shopping",
      notFoundTitle: "Order not found.",
      returnHomeText: "Return home",
    },
  },
  categories: [
    { key: "all-arrivals", label: "New arrivals", image: "", image_prompt: "Hero category tile for new arrivals — clean editorial flat-lay, neutral background. No text." },
    { key: "best-sellers", label: "Best sellers", image: "", image_prompt: "Hero category tile for best sellers — premium product styling, soft daylight. No text." },
    { key: "on-sale", label: "On sale", image: "", image_prompt: "Hero category tile for sale section — minimalist product styling. No text." },
  ],
  reviews: [
    { name: "Avery T.", text: "Beautiful packaging, exactly as described. Already on my second order.", rating: 5 },
    { name: "Jordan K.", text: "Quality is on another level for the price. Easy checkout too.", rating: 5 },
    { name: "Sasha L.", text: "Friendly support and fast shipping. I'll be back.", rating: 5 },
  ],
  trustBadges: [
    { icon: "Truck",       title: "Free shipping",   body: "Over $100" },
    { icon: "RefreshCw",   title: "30-day returns", body: "No questions" },
    { icon: "ShieldCheck", title: "Secure checkout", body: "SSL encrypted" },
  ],
  newsletter: {
    overline: "— Stay in the loop",
    heading: "10% off your first order.",
    body: "Subscribe for early access and a discount code on your first purchase.",
    placeholder: "Enter your email",
    submitText: "Sign up",
    successText: "Thanks! Check your inbox for the code.",
  },
  pricing: {
    currency: "USD",
    currencySymbol: "$",
    freeShippingThreshold: 100,
    flatShipping: 8,
  },
  seo: {
    title: "",
    description: "",
  },
  /**
   * Generic 2-axis variant model. Products can declare their own `variantAxes`;
   * if they don't, these defaults apply. Each axis is `{ key, label, values }`.
   * An empty array means the product has no variants and add-to-cart is direct.
   */
  variantAxesDefaults: [],

  /**
   * Demo products — kept tiny on purpose. Used as a fallback when the live
   * `/api/products` endpoint isn't wired up yet so the template still renders
   * with content. Real customer data is fetched from the backend at runtime.
   * Shape: { id, slug, name, category, price, compare_at_price?, rating,
   *          review_count, stock, featured, images: [], description,
   *          variantAxes?: [{ key, label, values }] }
   */
  demoProducts: [
    {
      id: "demo-1", slug: "signature-product-1", name: "Signature product",
      category: "best-sellers", price: 49, rating: 4.9, review_count: 142,
      stock: 24, featured: true, images: [""],
      description: "Our flagship product, made for everyday use.",
    },
    {
      id: "demo-2", slug: "signature-product-2", name: "Everyday essential",
      category: "best-sellers", price: 29, rating: 4.8, review_count: 87,
      stock: 18, featured: true, images: [""],
      description: "A simple favourite that goes with everything.",
    },
    {
      id: "demo-3", slug: "signature-product-3", name: "Limited edition",
      category: "on-sale", price: 39, compare_at_price: 59, rating: 4.9, review_count: 56,
      stock: 6, featured: true, images: [""],
      description: "Small-batch release while supplies last.",
    },
    {
      id: "demo-4", slug: "signature-product-4", name: "Gift pick",
      category: "all-arrivals", price: 64, rating: 5.0, review_count: 33,
      stock: 12, featured: true, images: [""],
      description: "A thoughtful gift, ready to wrap.",
    },
  ],
};


// ────────────────────────────────────────────────────────────────────────────
// PRESETS — one entry per supported industry. Each preset only overrides
// what's different from DEFAULT_TEMPLATE_CONFIG; `getTemplateConfig` does the
// deep-merge so callers always get a fully populated config.
// ────────────────────────────────────────────────────────────────────────────

export const TEMPLATE_PRESETS = {
  coffee: {
    business: { name: "Brew & Bean", tagline: "Single-origin coffee, roasted weekly." },
    hero: {
      overline: "— Spring lot · just landed",
      heading: "Coffee, the way it should taste.",
      subheading: "Single-origin beans, roasted in small batches. Shipped within 48 hours of roasting.",
      ctaText: "Shop the roast",
      ctaLink: "/shop",
      image_prompt: "Editorial coffee bag hero — warm amber morning light streaming over a wooden counter, a single artisanal coffee bag, ceramic cup with espresso, scattered roasted coffee beans. Magazine quality. No text, no logos.",
    },
    copy: {
      home: {
        featuredHeading: "Featured roasts.",
        editorialOverline: "— The roast philosophy",
        editorialHeading: "Roasted Wednesday, shipped Thursday.",
        editorialBody: "We don't roast in advance. Every order leaves our roastery within 48 hours of meeting the heat — so it lands at peak flavour.",
        editorialCta: "Our process",
        editorialImagePrompt: "Editorial photograph of a barista pouring espresso, warm natural light, magazine quality. No text.",
        reviewsHeading: "Tasted by coffee lovers.",
      },
      shop: { defaultHeading: "All coffee" },
      productDetail: {
        bullets: ["100% Arabica beans", "Single-origin, traceable", "Roasted within 48 hours of shipping", "Resealable foil bag"],
      },
    },
    categories: [
      { key: "whole-bean",   label: "Whole bean",   image_prompt: "Whole-bean coffee category tile — close-up of dark roasted beans, soft daylight. No text." },
      { key: "ground",       label: "Ground",       image_prompt: "Ground coffee category tile — coffee grounds in a brass scoop, warm tones. No text." },
      { key: "cold-brew",    label: "Cold brew",    image_prompt: "Cold brew category tile — bottle of cold brew on ice, condensation, summer light. No text." },
      { key: "subscription", label: "Subscription", image_prompt: "Coffee subscription category tile — three brown coffee bags lined up on a wooden shelf. No text." },
    ],
    reviews: [
      { name: "Marcus T.", text: "The Ethiopian roast is unreal. Bright, fruity, exactly what the bag said.", rating: 5 },
      { name: "Priya S.",  text: "Shipping is fast and the beans always arrive fresh. My weekly ritual.", rating: 5 },
      { name: "Liam W.",   text: "Tried 4 roasters this year — Brew & Bean is the only one I re-ordered.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "roast", label: "Roast", values: ["Light", "Medium", "Dark"] },
      { key: "grind", label: "Grind", values: ["Whole bean", "Espresso", "Filter", "French press"] },
    ],
  },

  candles: {
    business: { name: "Ember & Wick", tagline: "Hand-poured soy candles." },
    hero: {
      overline: "— Winter collection",
      heading: "Light a room, slow a moment.",
      subheading: "Hand-poured soy candles with carefully blended fragrance oils. Clean-burning, 50+ hours.",
      ctaText: "Shop the scents",
      image_prompt: "Editorial candle hero — soft beige tones, a lit ceramic-vessel candle on a linen tablecloth, warm winter light. Magazine quality. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured scents.",
        editorialHeading: "Soy wax, made by hand.",
        editorialBody: "Every candle is poured in our Brooklyn studio. We use 100% soy wax and lead-free cotton wicks — so what burns is exactly what we put in.",
        editorialImagePrompt: "Candle making studio — overhead shot of soy wax being poured into ceramic vessels, soft natural light, magazine quality. No text.",
        reviewsHeading: "Loved by 12,000+ customers.",
      },
      shop: { defaultHeading: "All candles" },
      productDetail: {
        bullets: ["100% soy wax", "Lead-free cotton wick", "50+ hour burn time", "Hand-poured in Brooklyn"],
      },
    },
    categories: [
      { key: "soy-candles",    label: "Soy candles",    image_prompt: "Soy candle category tile — three ceramic candles in warm tones, soft daylight. No text." },
      { key: "reed-diffusers", label: "Reed diffusers", image_prompt: "Reed diffuser category tile — minimalist amber glass diffuser, natural reeds, neutral background. No text." },
      { key: "wax-melts",      label: "Wax melts",      image_prompt: "Wax melt category tile — coloured wax melts arranged on linen, soft daylight. No text." },
      { key: "gift-sets",      label: "Gift sets",      image_prompt: "Candle gift set tile — a curated trio in matching neutral packaging, magazine quality. No text." },
    ],
    reviews: [
      { name: "Amelia R.", text: "The Cedar & Smoke is everything. Burns clean, smells incredible.", rating: 5 },
      { name: "Noah V.",   text: "Bought as a gift, ended up keeping it. Beautiful vessel too.", rating: 5 },
      { name: "Riley M.",  text: "Easily the best soy candle I've owned. Re-ordering twice over.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "scent", label: "Scent", values: ["Cedar & Smoke", "Linen", "Bergamot", "Fig & Vanilla"] },
      { key: "size",  label: "Size",  values: ["4 oz", "8 oz", "12 oz"] },
    ],
  },

  skincare: {
    business: { name: "Lumen Skincare", tagline: "Plant-powered, clinically tested." },
    hero: {
      overline: "— Botanical, vegan, dermatologist-tested",
      heading: "Skincare that quietly works.",
      subheading: "Clean formulations with proven active ingredients. No filler. Just what your skin needs.",
      ctaText: "Shop skincare",
      image_prompt: "Editorial skincare hero — minimalist composition with glass dropper bottles, a sprig of botanical greenery, soft pastel daylight. Magazine quality. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured formulas.",
        editorialHeading: "Tested by science, made from plants.",
        editorialBody: "Every formula goes through 8 weeks of clinical testing. We publish the results, not the marketing.",
        editorialImagePrompt: "Editorial photograph of a hand applying serum from a dropper, soft natural daylight, magazine quality. No text.",
        reviewsHeading: "Loved by 50,000+ skincare obsessives.",
      },
      shop: { defaultHeading: "All skincare" },
      productDetail: {
        bullets: ["Clinically tested formula", "Vegan and cruelty-free", "Fragrance-free", "Made in small batches"],
      },
    },
    categories: [
      { key: "cleansers",    label: "Cleansers",    image_prompt: "Skincare cleanser category tile — minimalist amber pump bottle, soft pastel background. No text." },
      { key: "serums",       label: "Serums",       image_prompt: "Skincare serum category tile — glass dropper bottle, botanical leaf, magazine quality. No text." },
      { key: "moisturizers", label: "Moisturizers", image_prompt: "Skincare moisturiser category tile — porcelain jar, soft natural daylight, neutral palette. No text." },
      { key: "masks",        label: "Masks",        image_prompt: "Skincare mask category tile — clay mask in a glass bowl with a bamboo applicator, neutral background. No text." },
    ],
    reviews: [
      { name: "Sasha L.", text: "Genuinely cleared my skin in 6 weeks. No irritation.", rating: 5 },
      { name: "Devon P.", text: "Lightweight, no fragrance, doesn't pill under sunscreen. Hard to find these.", rating: 5 },
      { name: "Mia K.",   text: "Bought one bottle to try. Now I've replaced my whole routine.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "size",      label: "Size",      values: ["15 ml", "30 ml", "50 ml"] },
      { key: "skin_type", label: "Skin type", values: ["Normal", "Dry", "Oily", "Combination"] },
    ],
  },

  clothing: {
    business: { name: "North Heavyweight", tagline: "Built to outlast trends." },
    hero: {
      overline: "— Drop 03",
      heading: "Built like it used to be.",
      subheading: "Heavyweight cotton. Boxy fit. Garment-dyed for a one-of-one finish.",
      ctaText: "Shop the drop",
      image_prompt: "Editorial streetwear hero — model in heavyweight hoodie standing against a concrete wall, golden hour, magazine quality. No text.",
    },
    copy: {
      home: {
        featuredOverline: "— Drop 03",
        featuredHeading: "Featured pieces.",
        editorialOverline: "— The heavyweight story",
        editorialHeading: "500gsm. Garment dyed. Made to last a decade.",
        editorialBody: "Every piece begins as raw heavyweight cotton, then garment-dyed in small batches for that one-of-one finish.",
        editorialCta: "Shop heavyweight",
        editorialImagePrompt: "Editorial fashion photograph — close-up of garment-dyed heavyweight cotton fabric texture, warm natural light. No text.",
        reviewsHeading: "6,500+ five-star reviews.",
      },
      shop: { defaultHeading: "Shop All" },
      productDetail: {
        bullets: ["Heavyweight 100% organic cotton", "Garment-dyed in small batches", "Reinforced stitching for years of wear", "Ethically made in Portugal"],
      },
    },
    categories: [
      { key: "tees",        label: "Tees",        image_prompt: "Heavyweight tee category tile — folded black tee on neutral background, magazine quality. No text." },
      { key: "hoodies",     label: "Hoodies",     image_prompt: "Heavyweight hoodie category tile — folded cream hoodie, soft natural daylight. No text." },
      { key: "jackets",     label: "Jackets",     image_prompt: "Bomber jacket category tile — hanging jacket on a minimalist wall, warm tones. No text." },
      { key: "bottoms",     label: "Bottoms",     image_prompt: "Heavyweight bottoms category tile — folded charcoal cargo pants on a wooden table. No text." },
    ],
    reviews: [
      { name: "Marcus T.", text: "Heaviest tee I own. Fit is perfect — washed it 20 times, still looks brand new.", rating: 5 },
      { name: "Jordan K.", text: "Bomber jacket runs true to size. Quality is on another level for the price.", rating: 5 },
      { name: "Sasha L.",  text: "Finally, a brand that gets the boxy fit right. Cart drawer was smooth too.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "size",  label: "Size",  values: ["XS", "S", "M", "L", "XL", "XXL"] },
      { key: "color", label: "Colour", values: ["Black", "Bone", "Charcoal", "Olive"] },
    ],
  },

  jewelry: {
    business: { name: "Halo & Hammer", tagline: "Handmade jewelry, made to be worn every day." },
    hero: {
      overline: "— New designs",
      heading: "Quiet pieces, worn every day.",
      subheading: "Solid 14k gold and sterling silver. Made by hand, designed to last.",
      ctaText: "Shop jewelry",
      image_prompt: "Editorial jewelry hero — fine gold necklace and ring resting on a marble surface, soft directional daylight. Magazine quality. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured pieces.",
        editorialHeading: "Solid metals, no plating.",
        editorialBody: "Plating wears off. Ours doesn't. Every piece is solid 14k gold or sterling silver, so it lasts as long as you do.",
        editorialImagePrompt: "Close-up editorial photo of a goldsmith's hands shaping a delicate ring, warm workshop light. No text.",
        reviewsHeading: "Worn by 25,000+ customers.",
      },
      shop: { defaultHeading: "All jewelry" },
      productDetail: {
        bullets: ["Solid 14k gold or sterling silver", "Hand-finished in our studio", "Hypoallergenic", "Lifetime cleaning included"],
      },
    },
    categories: [
      { key: "necklaces", label: "Necklaces", image_prompt: "Necklace category tile — delicate gold chain on marble, soft daylight. No text." },
      { key: "rings",     label: "Rings",     image_prompt: "Ring category tile — minimalist gold ring on a velvet cushion. No text." },
      { key: "earrings",  label: "Earrings",  image_prompt: "Earrings category tile — pair of small gold hoops on neutral background. No text." },
      { key: "bracelets", label: "Bracelets", image_prompt: "Bracelet category tile — single silver bangle on a marble surface. No text." },
    ],
    reviews: [
      { name: "Elena V.",  text: "Wore my necklace in the ocean for two weeks. Still shines like day one.", rating: 5 },
      { name: "Cassie M.", text: "Sized perfectly the first time. Beautiful packaging.", rating: 5 },
      { name: "Theo R.",   text: "Bought as a gift, my partner hasn't taken it off in a month.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "size",  label: "Size",   values: ["5", "6", "7", "8", "9"] },
      { key: "metal", label: "Metal",  values: ["14k Gold", "Sterling Silver", "Rose Gold"] },
    ],
  },

  home_goods: {
    business: { name: "Linenwood Home", tagline: "Quietly beautiful things for everyday life." },
    hero: {
      overline: "— Spring refresh",
      heading: "Made for the way you live.",
      subheading: "Linen, ceramic, wood — natural materials, finished by hand.",
      ctaText: "Shop home",
      image_prompt: "Editorial home goods hero — warm sun-lit living room with linen throw on a cream sofa and a handmade ceramic vase. Magazine quality. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured pieces.",
        editorialHeading: "Real materials, made by makers.",
        editorialBody: "Every piece in our shop is made by an artisan we've met. We pay them fairly and we list their name.",
        editorialImagePrompt: "Editorial photograph of a potter's hands shaping a ceramic vessel on a wheel, warm studio light. No text.",
        reviewsHeading: "In homes everywhere.",
      },
      shop: { defaultHeading: "All home" },
      productDetail: {
        bullets: ["Made by hand", "Natural materials", "Free of synthetic finishes", "Designed to age beautifully"],
      },
    },
    categories: [
      { key: "kitchen",  label: "Kitchen",  image_prompt: "Kitchen category tile — handmade ceramic mug on a wooden cutting board, natural light. No text." },
      { key: "bedroom",  label: "Bedroom",  image_prompt: "Bedroom category tile — neatly folded linen sheets in oatmeal tones. No text." },
      { key: "living",   label: "Living",   image_prompt: "Living category tile — folded throw blanket on a linen sofa, warm light. No text." },
      { key: "decor",    label: "Decor",    image_prompt: "Decor category tile — sculptural ceramic vase with dried grass, magazine quality. No text." },
    ],
    reviews: [
      { name: "Sloane W.", text: "The linen sheets get softer every wash. Best money I've spent on bedding.", rating: 5 },
      { name: "Andrew P.", text: "Beautiful piece, beautifully packaged. Felt like opening a gift to myself.", rating: 5 },
      { name: "Marisa B.", text: "The hand-thrown bowls have so much character. Use them daily.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "size",  label: "Size",  values: ["Small", "Medium", "Large"] },
      { key: "color", label: "Colour", values: ["Natural", "Charcoal", "Sage", "Oat"] },
    ],
  },

  pet: {
    business: { name: "Good Dog Co.", tagline: "Treats and toys made by people who love dogs." },
    hero: {
      overline: "— New arrivals",
      heading: "Made for the goodest dogs.",
      subheading: "Real ingredients, durable toys, and treats your vet would approve of.",
      ctaText: "Shop dog",
      image_prompt: "Editorial pet brand hero — a happy golden retriever lying on a linen rug with a chew toy and a bowl of natural treats. Warm sunlight. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured for dogs.",
        editorialHeading: "Better food. Better walks. Better dogs.",
        editorialBody: "We don't make filler treats. Every product is reviewed by a vet and tested by our own dogs first.",
        editorialImagePrompt: "Editorial photo of a hand holding out a natural dog treat to a small dog, soft daylight. No text.",
        reviewsHeading: "Loved by dogs (and their humans).",
      },
      shop: { defaultHeading: "Shop everything" },
      productDetail: {
        bullets: ["Real ingredients, no fillers", "Vet-approved", "Sourced ethically", "Tested by our own dogs"],
      },
    },
    categories: [
      { key: "food",     label: "Food",     image_prompt: "Pet food category tile — bowl of natural kibble next to a small bag, soft daylight. No text." },
      { key: "toys",     label: "Toys",     image_prompt: "Dog toy category tile — a rope ball toy on a neutral background, magazine quality. No text." },
      { key: "beds",     label: "Beds",     image_prompt: "Dog bed category tile — plush oatmeal bed with a small chew toy, warm light. No text." },
      { key: "grooming", label: "Grooming", image_prompt: "Pet grooming category tile — natural shampoo bottle and grooming brush, neutral background. No text." },
    ],
    reviews: [
      { name: "Helen D.", text: "My picky senior dog actually eats this. Two months in, his coat looks amazing.", rating: 5 },
      { name: "Tom P.",   text: "The chew toy is genuinely indestructible. Worth every dollar.", rating: 5 },
      { name: "Casey J.", text: "Fast shipping and our cocker loves the bed. So does the cat.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "size",   label: "Size",   values: ["Small", "Medium", "Large"] },
      { key: "flavor", label: "Flavour", values: ["Chicken", "Beef", "Salmon", "Lamb"] },
    ],
  },

  handmade: {
    business: { name: "Slow Studio", tagline: "Small-batch, handmade goods." },
    hero: {
      overline: "— Made by hand",
      heading: "Slow goods for fast lives.",
      subheading: "Every piece is made in small batches by independent artisans we know personally.",
      ctaText: "Shop handmade",
      image_prompt: "Editorial handmade goods hero — hands shaping clay on a potter's wheel in a warm-lit studio. Magazine quality. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured makers.",
        editorialHeading: "One person. One bench. One piece at a time.",
        editorialBody: "Mass production has its place — but not here. Every product is made by a real maker we work with directly.",
        editorialImagePrompt: "Editorial workshop photograph — a leatherworker stitching by hand in a warm-toned studio. No text.",
        reviewsHeading: "Made with love, kept for years.",
      },
      shop: { defaultHeading: "All goods" },
      productDetail: {
        bullets: ["Made by hand", "Small-batch — slight variation is part of it", "Sourced from independent makers", "Designed to be kept"],
      },
    },
    categories: [
      { key: "ceramics", label: "Ceramics", image_prompt: "Ceramics category tile — a hand-thrown mug on a wooden shelf, warm light. No text." },
      { key: "textiles", label: "Textiles", image_prompt: "Textile category tile — handwoven throw blanket folded on linen, soft daylight. No text." },
      { key: "wood",     label: "Wood",     image_prompt: "Woodwork category tile — a hand-carved bowl on a marble surface, magazine quality. No text." },
      { key: "paper",    label: "Paper",    image_prompt: "Paper category tile — handmade notebook with leather cover, neutral background. No text." },
    ],
    reviews: [
      { name: "Sienna G.", text: "Every piece feels intentional. I keep going back.", rating: 5 },
      { name: "Wes H.",    text: "The little story card that comes with each piece is a lovely touch.", rating: 5 },
      { name: "Avery R.",  text: "Bought a ceramic bowl. Have used it every day for six months. Still my favourite.", rating: 5 },
    ],
    variantAxesDefaults: [],
  },

  food: {
    business: { name: "Pantry Co.", tagline: "Small-batch pantry essentials." },
    hero: {
      overline: "— Stocked weekly",
      heading: "Real food. Real ingredients. Really good.",
      subheading: "From the makers we'd buy from ourselves. Small batch, ethically sourced.",
      ctaText: "Shop pantry",
      image_prompt: "Editorial food brand hero — wooden table with artisan olive oil bottle, fresh herbs, and a loaf of sourdough. Magazine quality. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured pantry.",
        editorialHeading: "Made by people who eat what they sell.",
        editorialBody: "Our shop only carries food we'd put on our own table. Every product is tasted, vetted, and chosen with care.",
        editorialImagePrompt: "Editorial food photograph — wooden cutting board with fresh-baked bread, olive oil, and herbs, warm daylight. No text.",
        reviewsHeading: "Loved at the dinner table.",
      },
      shop: { defaultHeading: "All pantry" },
      productDetail: {
        bullets: ["Small-batch", "Real ingredients, no preservatives", "Ethically sourced", "Made by makers we know"],
      },
    },
    categories: [
      { key: "pantry",      label: "Pantry",      image_prompt: "Pantry category tile — artisan olive oil bottle next to a small bowl of sea salt, magazine quality. No text." },
      { key: "snacks",      label: "Snacks",      image_prompt: "Snacks category tile — bowl of nuts and granola on a wooden board, warm light. No text." },
      { key: "drinks",      label: "Drinks",      image_prompt: "Drinks category tile — bottle of cold-pressed juice with citrus, soft daylight. No text." },
      { key: "gift-boxes",  label: "Gift boxes",  image_prompt: "Gift box category tile — curated wooden gift box with various food items, neutral background. No text." },
    ],
    reviews: [
      { name: "Greta B.",  text: "The olive oil changed how I cook. I'll never buy supermarket again.", rating: 5 },
      { name: "Owen L.",   text: "Fast shipping, beautiful packaging, and everything tastes great.", rating: 5 },
      { name: "Hattie K.", text: "Bought the gift box for my mum. She immediately asked for the address.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "size", label: "Size", values: ["Small", "Family", "Bulk"] },
    ],
  },

  art_prints: {
    business: { name: "Paper & Pigment", tagline: "Original art, printed for your walls." },
    hero: {
      overline: "— New collection",
      heading: "Art for the walls you actually live with.",
      subheading: "Limited-edition prints by independent artists. Museum-quality paper, sustainably printed.",
      ctaText: "Shop prints",
      image_prompt: "Editorial gallery hero — a framed minimalist art print on a textured plaster wall, golden hour light. Magazine quality. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured prints.",
        editorialHeading: "Each artist. Each print. Each story.",
        editorialBody: "We work directly with artists. They earn 50% of every sale, and we publish their name on the print certificate.",
        editorialImagePrompt: "Editorial photograph of a printmaker pulling a fresh screenprint, warm studio light. No text.",
        reviewsHeading: "On walls around the world.",
      },
      shop: { defaultHeading: "All prints" },
      productDetail: {
        bullets: ["Museum-quality archival paper", "Hand-numbered, signed editions", "Sustainably printed", "Ships flat in a protective tube"],
      },
    },
    categories: [
      { key: "photography",   label: "Photography",   image_prompt: "Photography prints category tile — black and white photo art print on a wall. No text." },
      { key: "illustration",  label: "Illustration",  image_prompt: "Illustration print category tile — colourful illustrated art print on a marble shelf. No text." },
      { key: "abstract",      label: "Abstract",      image_prompt: "Abstract art print category tile — minimalist abstract framed print, neutral wall. No text." },
      { key: "typography",    label: "Typography",    image_prompt: "Typography print category tile — large-format typography art print, magazine quality. No text." },
    ],
    reviews: [
      { name: "Jules W.", text: "Print quality is unreal. Looks like a gallery piece on my wall.", rating: 5 },
      { name: "Eli H.",   text: "Came perfectly packed. Loved the artist note that came with it.", rating: 5 },
      { name: "Naomi C.", text: "Bought three prints. They all hang together beautifully.", rating: 5 },
    ],
    variantAxesDefaults: [
      { key: "size",  label: "Size",  values: ['8" × 10"', '11" × 14"', '16" × 20"', '24" × 36"'] },
      { key: "frame", label: "Frame", values: ["None", "Black", "White", "Natural"] },
    ],
  },

  digital_products: {
    business: { name: "Studio Files", tagline: "Digital tools that save your day." },
    hero: {
      overline: "— New release",
      heading: "Made by designers. For designers.",
      subheading: "Premium templates, presets, and digital assets. Instant download — no shipping wait.",
      ctaText: "Browse files",
      image_prompt: "Editorial digital products hero — a laptop on a clean desk showing a design template, warm natural light, magazine quality. No text.",
    },
    copy: {
      home: {
        featuredHeading: "Featured downloads.",
        editorialHeading: "Built for working designers.",
        editorialBody: "Every file is built by a working designer for working designers. Practical, well-organised, and tested in real client projects.",
        editorialImagePrompt: "Editorial photograph of a designer's workspace — laptop, sketchbook, coffee, warm directional light. No text.",
        reviewsHeading: "Trusted by 30,000+ creators.",
      },
      shop: { defaultHeading: "All files" },
      productDetail: {
        bullets: ["Instant download — no shipping", "Lifetime updates included", "Commercial use license", "Works in Figma, Adobe, Procreate, etc."],
      },
      orderSuccess: {
        whatsNextTitle: "Your download",
        emailLine: "Your files are also waiting in your inbox — check spam if you don't see it.",
        shippingLine: "Lifetime updates ship to the same email whenever we publish a new version.",
      },
    },
    categories: [
      { key: "templates", label: "Templates", image_prompt: "Digital templates category tile — laptop screen showing a clean design template, warm desk. No text." },
      { key: "presets",   label: "Presets",   image_prompt: "Photo preset category tile — a phone showing a preset comparison, soft daylight. No text." },
      { key: "ebooks",    label: "eBooks",    image_prompt: "eBook category tile — tablet with an ebook cover open, on a wooden desk. No text." },
      { key: "courses",   label: "Courses",   image_prompt: "Online course category tile — laptop screen showing a course lesson, warm focused light. No text." },
    ],
    reviews: [
      { name: "Talia R.", text: "Worth every penny. Saved me hours on the very first project.", rating: 5 },
      { name: "Sam D.",   text: "Instant download, well-organised files, lovely typography. Will buy again.", rating: 5 },
      { name: "Quinn V.", text: "Lifetime updates is a huge plus. Already had two new versions roll in.", rating: 5 },
    ],
    pricing: { freeShippingThreshold: 0, flatShipping: 0 },
    trustBadges: [
      { icon: "Truck",       title: "Instant download", body: "No shipping wait" },
      { icon: "RefreshCw",   title: "Lifetime updates", body: "Free forever" },
      { icon: "ShieldCheck", title: "Secure checkout",  body: "SSL encrypted" },
    ],
    variantAxesDefaults: [],
  },
};


// ────────────────────────────────────────────────────────────────────────────
// Public helper — produce a fully populated config by deep-merging
// DEFAULT ⨯ preset ⨯ overrides.
// ────────────────────────────────────────────────────────────────────────────

function isPlainObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function deepMerge(...sources) {
  const out = {};
  for (const src of sources) {
    if (!src) continue;
    for (const k of Object.keys(src)) {
      if (isPlainObject(src[k]) && isPlainObject(out[k])) {
        out[k] = deepMerge(out[k], src[k]);
      } else if (src[k] !== undefined) {
        out[k] = src[k];
      }
    }
  }
  return out;
}

/**
 * Build a template config for the given industry. Pass overrides to layer the
 * customer's business-specific copy/images on top.
 *
 * @param {string} industryKey  e.g. "coffee", "candles", "skincare", "clothing", "jewelry",
 *                              "home_goods", "pet", "handmade", "food", "art_prints",
 *                              "digital_products". Unknown keys fall back to defaults.
 * @param {object} overrides    optional deep partial that wins over preset + default.
 * @returns {object}            fully populated config
 */
export function getTemplateConfig(industryKey, overrides = {}) {
  const preset = TEMPLATE_PRESETS[industryKey] || {};
  return deepMerge(DEFAULT_TEMPLATE_CONFIG, preset, overrides);
}

export const SUPPORTED_INDUSTRIES = Object.keys(TEMPLATE_PRESETS);
